var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-tiptap.ts
import { css } from "@adornis/chemistry/directives/css.js";
import { injectAttributesOf } from "@adornis/chemistry/directives/inject-attributes.js";
import "@adornis/chemistry/elements/components/x-formfield-wrapper.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { fontReset } from "@adornis/fonts/font-resets.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { Editor } from "@tiptap/core";
import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import "./components/x-tiptap-action-bar.js";
import { isTiptapExtension, isTransformer } from "./extensions/ExtensionBase.js";
import { starterPack } from "./extensions/starterPack.js";
function getStylesFromExtensions(host, exts) {
  return exts.flatMap((extension) => extension.styles?.({ host }) ?? []);
}
var XTiptap = class extends FormField {
  constructor() {
    super(...arguments);
    this.extensions = starterPack;
    this.mode = "html";
    this.plain = false;
    this.translator = new TranslationController(this);
    this.hideToolbar = false;
    this._container = createRef();
    this._actionBar = createRef();
    this._flatExtensions = [];
    this._tiptapExtensions = [];
    this._transformers = [];
  }
  connectedCallback() {
    super.connectedCallback();
    this._flatExtensions = this.extensions.flat();
    this._tiptapExtensions = this._flatExtensions.flatMap((extension) => extension.extensions.filter(isTiptapExtension));
    this._transformers = this._flatExtensions.flatMap((extension) => extension.extensions.filter(isTransformer));
  }
  getEditor() {
    return this._editor;
  }
  getTransformer() {
    const transformer = this._transformers.find((t) => t.type === this.mode);
    if (!transformer)
      throw new Error(`mode ${this.mode} is unsupported`);
    return transformer;
  }
  transformContent() {
    return this.getTransformer().toContent(this.value.value || null, this._tiptapExtensions);
  }
  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties);
    if (!this._container.value)
      return console.warn("No container found for tiptap editor");
    this._editor = new Editor({
      element: this._container.value,
      extensions: this._tiptapExtensions,
      injectCSS: false,
      content: this.transformContent(),
      editable: !this.disabled,
      editorProps: {
        transformPastedText: (text) => {
          return text.replace(/\xA0/g, " ");
        }
      },
      onCreate: ({ editor }) => {
        this.dispatchEvent(new CustomEvent("editor-created", { detail: { editor } }));
      },
      onUpdate: ({ editor, transaction }) => {
        if (!transaction.docChanged)
          return;
        const json = editor.getJSON();
        const value = this.getTransformer().toValue(json, this._tiptapExtensions);
        this.value.next(value);
        this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value, mode: this.mode } }));
      }
    });
    if (this._actionBar.value)
      this._actionBar.value.editor = this._editor;
    this.disconnected.subscribe(() => {
      this._editor?.destroy();
    });
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has("value")) {
      const editor = this._editor;
      if (!editor)
        return;
      const { from, to } = editor.state.selection;
      if (this.value.value !== this.getTransformer().toValue(editor.getJSON(), this._tiptapExtensions)) {
        editor.commands.setContent(this.transformContent(), false);
        editor.commands.setTextSelection({ from, to });
      }
    }
    if (changedProperties.has("disabled")) {
      this._editor?.setEditable(!this.disabled);
    }
  }
  render() {
    const plain = html`
      <x-flex space="sm">
        ${this._actionbar()}
        <style>
          ${getStylesFromExtensions(this, this._flatExtensions).filter((style) => typeof style === "string")}
        </style>
        <div class="tiptap-container" ${ref(this._container)}></div>
      </x-flex>
    `;
    if (this.plain)
      return xComponents(plain);
    return xComponents(html`
      <x-formfield-wrapper multi-line ${injectAttributesOf(this)} ?disabled=${false}> ${plain} </x-formfield-wrapper>
    `);
  }
  _actionbar() {
    if (this.disabled || this.hideToolbar)
      return nothing;
    return html`
      <x-tiptap-action-bar
        .extensions=${this.extensions}
        .editor=${this._editor}
        ${ref(this._actionBar)}
        ${css({ zIndex: 1 })}
      ></x-tiptap-action-bar>
    `;
  }
  styles() {
    const extensionStyles = getStylesFromExtensions(this, this._flatExtensions).filter(
      (style) => typeof style !== "string"
    );
    return [
      ...super.styles(),
      {
        ":host": { display: "block" },
        ".tiptap.ProseMirror": {
          "&:focus-visible": { outline: "none" }
        },
        ol: {
          ...fontReset(this.fontFamily, 1, 400, 1.2, this.baseFontSize)
        },
        table: {
          borderSpacing: 0
        },
        th: {
          backgroundColor: "rgba(0,0,0,0.1)"
        },
        "th, td": {
          border: "1px solid rgba(0,0,0,0.1)",
          padding: `0 ${this.spacing.xs}`
        }
      },
      ...extensionStyles
    ];
  }
};
__decorateClass([
  property({ attribute: false })
], XTiptap.prototype, "extensions", 2);
__decorateClass([
  property({ attribute: true })
], XTiptap.prototype, "mode", 2);
__decorateClass([
  property({ attribute: true, type: Boolean })
], XTiptap.prototype, "plain", 2);
__decorateClass([
  property({ attribute: "hide-toolbar", type: Boolean })
], XTiptap.prototype, "hideToolbar", 2);
XTiptap = __decorateClass([
  customElement("x-tiptap")
], XTiptap);
export {
  XTiptap
};
